<template>
  <!-- MODAL DE GESTIÓN DE CUENTAS -->
  <div v-if="isOpen" class="fixed inset-0 z-70 overflow-hidden">
    <!-- Overlay de fondo -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>

    <!-- Container del modal -->
    <div class="fixed inset-0 flex items-center justify-center p-0">
      <div class="relative bg-white w-full h-full flex flex-col">

        <!-- HEADER DEL MODAL -->
        <div class="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-3 sticky top-0 z-10">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                </svg>
              </div>
              <div>
                <h2 class="text-base font-bold">Gestión de Usuarios</h2>
                <p class="text-indigo-100 text-sm">Administre todos los usuarios de efibox</p>
              </div>
            </div>
            <button @click="closeModal" class="w-8 h-8 rounded-full bg-indigo-500 hover:bg-indigo-600 flex items-center justify-center transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- CONTENIDO PRINCIPAL -->
        <div class="flex-1 overflow-y-auto">
          <div class="p-2">
            <!-- VISTA DE TABLA (Por defecto) -->
            <div v-show="!showForm" class="flex flex-col h-full">

              <!-- BARRA DE FILTROS Y ACCIONES -->
              <div class="bg-gray-50 border-b border-gray-200 p-4 flex-shrink-0">
                <div class="grid grid-cols-1 lg:grid-cols-4 gap-3">
                  <!-- Búsqueda y Contador -->
                  <div class="lg:col-span-2 space-y-3">
                    <div class="relative">
                      <input v-model="searchText" type="text" placeholder="Buscar usuarios..." class="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors">
                      <svg class="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                      </svg>
                      <button v-if="searchText" @click="searchText = ''" class="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                    <div class="flex items-end">
                      <div class="text-sm text-gray-600 whitespace-nowrap">
                        Se muestran {{ usuariosFiltrados.length }} resultados<span v-if="hasActiveFilters">, presione en
                          <button @click="clearAllFilters" class="text-red-600 underline hover:text-red-800 font-medium transition-colors">Limpiar</button> para quitar los filtros</span>.
                      </div>
                    </div>
                  </div>

                  <!-- Filtro por Empresa -->
                  <div>
                    <div class="relative">
                      <button @click="toggleDropdown('empresa')" class="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-left flex items-center justify-between">
                        <span class="text-gray-700 truncate">{{ selectedEmpresa ? getEmpresaNombrePorId(selectedEmpresa) : 'Todas las Empresas' }}</span>
                        <svg :class="['w-4 h-4 text-gray-400 transition-transform flex-shrink-0', dropdownOpen.empresa ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </button>
                      <div v-if="dropdownOpen.empresa" class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <button @click="selectOption('empresa', '')" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 first:rounded-t-lg">Todas</button>
                        <button v-for="empresa in empresasUnicas" :key="empresa.id" @click="selectOption('empresa', empresa.id)" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 truncate" :title="empresa.nombre_empresa">{{ empresa.nombre_empresa }}</button>
                      </div>
                    </div>
                  </div>

                  <!-- Filtro por Rol y Botones -->
                  <div class="flex flex-col gap-3">
                    <div class="relative">
                      <button @click="toggleDropdown('tipoUsuario')" class="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-left flex items-center justify-between">
                        <span class="text-gray-700 truncate">{{ selectedTipoUsuario ? getTipoUsuario(selectedTipoUsuario) : 'Todos los Roles' }}</span>
                        <svg :class="['w-4 h-4 text-gray-400 transition-transform', dropdownOpen.tipoUsuario ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </button>
                      <div v-if="dropdownOpen.tipoUsuario" class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <button @click="selectOption('tipoUsuario', '')" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 first:rounded-t-lg">Todos</button>
                        <button v-for="tipo in tiposUsuarioUnicos" :key="tipo.id" @click="selectOption('tipoUsuario', tipo.id)" class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50">{{ tipo.nombre }}</button>
                      </div>
                    </div>
                    <div class="flex items-center justify-end gap-2 mt-2">
                      <button @click="loadUsers" :disabled="loadingUsers" class="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50" title="Actualizar lista"><svg :class="['w-4 h-4', loadingUsers ? 'animate-spin' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg></button>
                      <button v-if="puedeCrearUsuario" @click="showCreateForm" class="bg-white text-center text-sm w-36 rounded-xl h-10 relative text-black font-medium group border border-indigo-600 transition-all duration-300 overflow-hidden">
                        <div class="flex items-center justify-center h-full relative z-10 transition-all duration-300 group-hover:text-white">
                          <svg class="w-4 h-4 mr-1.5 transition-all duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                          </svg>
                          <span>Añadir Usuario</span>
                        </div>
                        <div class="absolute inset-0 bg-indigo-600 rounded-xl transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tabla de usuarios -->
              <div v-if="usuariosFiltrados.length > 0" class="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="usuario in usuariosFiltrados" :key="usuario.id" class="hover:bg-gray-50 transition-colors">
                        <!-- Información del usuario -->
                        <td class="px-6 py-4">
                          <div class="flex items-center">
                            <div class="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                              <span class="text-indigo-600 text-sm font-bold">
                                {{ getInitials(usuario.nombre, usuario.apellido) }}
                              </span>
                            </div>
                            <div>
                              <div class="text-sm font-medium text-gray-900">
                                {{ usuario.nombre }} {{ usuario.apellido }}
                              </div>
                              <div class="text-sm text-gray-500">{{ usuario.email }}</div>
                              <div class="text-sm text-gray-500" v-if="usuario.rut">RUT: {{ usuario.rut }}</div>
                              <div class="text-sm text-gray-500" v-if="usuario.telefono">Tel: {{ formatTelefonoDisplay(usuario.telefono) }}</div>
                            </div>
                          </div>
                        </td>

                        <!-- Empresa -->
                        <td class="px-6 py-4">
                          <div class="text-sm text-gray-900">{{ getEmpresaNombre(usuario) }}</div>
                        </td>

                        <!-- Rol -->
                        <td class="px-6 py-4">
                          <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                            {{ getTipoUsuario(usuario.tipo_usuario) }}
                          </span>
                        </td>

                        <!--   ESTADO DEL USUARIO -->
                        <td class="px-6 py-4 text-center">
                          <span :class="[
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            usuario.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          ]">
                            <svg :class="[
                              'w-1.5 h-1.5 mr-1.5',
                              usuario.is_active ? 'text-green-400' : 'text-red-400'
                            ]" fill="currentColor" viewBox="0 0 8 8">
                              <circle cx="4" cy="4" r="3"></circle>
                            </svg>
                            {{ usuario.is_active ? 'Activo' : 'Inactivo' }}
                          </span>
                        </td>

                        <!-- Acciones -->
                        <td class="px-6 py-4 text-center">
                          <div class="flex items-center justify-center space-x-2">
                            <!-- Botón Editar -->
                            <button v-if="puedeEditarUsuario" @click="editUser(usuario)" :disabled="isLoading" class="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50" title="Editar usuario">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                              </svg>
                            </button>

                            <!-- Botón Eliminar -->
                            <button v-if="puedeEliminarUsuario" @click="showDeleteConfirmation(usuario)" :disabled="isLoading" class="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Eliminar usuario">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>

                      <!-- Estado vacío -->
                      <tr v-if="usuariosFiltrados.length === 0 && usuarios.length > 0">
                        <td colspan="5" class="px-6 py-12 text-center text-gray-500">
                          <div class="flex flex-col items-center">
                            <svg class="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                            </svg>
                            <p class="text-lg font-medium mb-2">No hay usuarios registrados</p>
                            <p class="text-sm mb-4">Comienza agregando el primer usuario a tu sistema</p>
                            <button v-if="puedeCrearUsuario" @click="showCreateForm" class="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors">
                              Crear primer usuario
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- VISTA DE FORMULARIO (Toggle) -->
            <div v-show="showForm" class="p-6 space-y-6">

              <!-- Header del formulario con botón volver -->
              <div class="flex items-center justify-between border-b border-gray-200 pb-4">
                <div>
                  <h3 class="text-xl font-semibold text-gray-900">
                    {{ isEditing ? 'Editar Usuario' : 'Nuevo Usuario' }}
                  </h3>
                  <p class="text-sm text-gray-500 mt-1">
                    {{ isEditing ? 'Modifica la información del usuario' : 'Completa todos los campos para registrar el usuario' }}
                  </p>
                </div>
                <button @click="cancelForm" class="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                  </svg>
                  <span>Volver a la lista</span>
                </button>
              </div>

              <!-- Formulario -->
              <form @submit.prevent="submitForm" class="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <!-- Columna izquierda -->
                <div class="space-y-4">

                  <!-- Email -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Correo Electrónico *
                    </label>
                    <input v-model="formData.email" type="email" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50" placeholder="usuario@empresa.com">
                  </div>

                  <!-- Nombre -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input v-model="formData.nombre" type="text" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50" placeholder="Juan">
                  </div>

                  <!-- Apellido -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Apellido *
                    </label>
                    <input v-model="formData.apellido" type="text" required :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50" placeholder="Erices">
                  </div>

                  <!-- RUT -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      RUT
                    </label>
                    <input v-model="formData.rut" @input="formatRut" type="text" :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50" placeholder="12.345.678-9">
                  </div>
                  <!-- Telefono -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input v-model="formData.telefono" type="tel" :disabled="isLoading" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50" placeholder="+56 9 1234 5678" @input="formatTelefono">
                  </div>
                </div>

                <!-- Columna derecha -->
                <div class="space-y-4">

                  <!-- Empresa -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Empresa *
                    </label>
                    <select v-model="formData.empresa_nombre" required :disabled="isLoading || loadingSelectEmpresas" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50">
                      <option value="">
                        {{ loadingSelectEmpresas ? 'Cargando empresas...' : '-- Seleccionar Empresa --' }}
                      </option>
                      <option v-for="selectEmpresa in selectEmpresas" :key="selectEmpresa.id" :value="selectEmpresa.nombre_empresa">
                        {{ selectEmpresa.nombre_empresa }}
                      </option>
                    </select>
                  </div>

                  <!-- Tipo de usuario -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Usuario *
                    </label>
                    <select v-model="formData.tipoUsuario_id" required :disabled="isLoading || loadingTipoUsuarios" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50">
                      <option value="">{{ loadingTipoUsuarios ? 'Cargando tipos...' : '-- Seleccionar Rol --' }}</option>
                      <option v-for="tipoUsuario in tipoUsuarios" :key="tipoUsuario.id" :value="tipoUsuario.id">
                        {{ tipoUsuario.nombre }}
                      </option>
                    </select>
                  </div>

                  <!-- Estado activo -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Estado del Usuario
                    </label>
                    <div class="flex items-center space-x-3">
                      <input v-model="formData.is_active" type="checkbox" :disabled="isLoading" class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
                      <span class="text-sm text-gray-700">Usuario activo</span>
                    </div>
                  </div>

                  <!--   NUEVO CAMPO: Local Asignado -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      <svg class="w-4 h-4 inline mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                      </svg>
                      Local Asignado (Opcional)
                    </label>
                    <select v-model="formData.local_asignado_id" :disabled="isLoading || loadingLocales || !formData.empresa_id" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors disabled:opacity-50">
                      <option value="">
                        {{ getOpcionesLocalTexto() }}
                      </option>
                      <option v-for="local in localesDisponibles" :key="local.id" :value="local.id">
                        Local {{ local.numero_local }} - {{ local.direccion.length > 50 ? local.direccion.substring(0, 50) + '...' : local.direccion }}
                      </option>
                    </select>

                    <!-- Información adicional -->
                    <div class="mt-2 text-xs text-gray-500 space-y-1">
                      <div class="flex items-center space-x-1">
                        <svg class="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <span>Solo se muestran locales sin encargado de la empresa seleccionada</span>
                      </div>

                      <div v-if="formData.empresa_id && localesDisponibles.length === 0 && !loadingLocales" class="flex items-center space-x-1 text-amber-600">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                        </svg>
                        <span>Todos los locales de esta empresa ya tienen encargado asignado</span>
                      </div>

                      <div v-if="loadingLocales" class="flex items-center space-x-1 text-blue-600">
                        <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Cargando locales disponibles...</span>
                      </div>

                      <div v-if="formData.local_asignado_id" class="flex items-center space-x-1 text-green-600">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span>Este usuario será asignado como encargado del local seleccionado</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Botones del formulario -->
                <div class="lg:col-span-2 flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button type="button" @click="cancelForm" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit" :disabled="isLoading || !isFormValid" class="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
                    <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{{ isLoading ? 'Procesando...' : (isEditing ? 'Actualizar Usuario' : 'Crear Usuario') }}</span>
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ========================================== -->
  <!--   MODAL DE CONFIRMACIÓN DE ELIMINACIÓN -->
  <!-- ========================================== -->
  <div v-if="showDeleteModal" class="fixed inset-0 z-50" style="z-index: 9999;">
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

    <div class="fixed inset-0 flex items-center justify-center p-4">
      <div class="relative bg-white rounded-lg w-full max-w-md">

        <!-- Header del modal -->
        <div class="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 rounded-t-lg">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold">Confirmar Eliminación</h3>
              <p class="text-red-100 text-sm">Esta acción no se puede deshacer</p>
            </div>
          </div>
        </div>

        <!-- Contenido del modal -->
        <div class="p-6">
          <div class="text-center">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
              </svg>
            </div>

            <h4 class="text-lg font-semibold text-gray-900 mb-2">
              ¿Eliminar usuario?
            </h4>

            <div v-if="userToDelete" class="bg-gray-50 rounded-lg p-4 mb-4">
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <span class="text-red-600 text-sm font-bold">
                    {{ getInitials(userToDelete.nombre, userToDelete.apellido) }}
                  </span>
                </div>
                <div class="text-left">
                  <div class="font-medium text-gray-900">
                    {{ userToDelete.nombre }} {{ userToDelete.apellido }}
                  </div>
                  <div class="text-sm text-gray-500">{{ userToDelete.email }}</div>
                  <div class="text-sm text-gray-500">{{ getTipoUsuario(userToDelete.tipo_usuario) }}</div>
                </div>
              </div>
            </div>

            <p class="text-gray-600 mb-6">
              Esta acción eliminará permanentemente al usuario y toda su información asociada.
              No podrás recuperar estos datos posteriormente.
            </p>
          </div>

          <!-- Botones del modal -->
          <div class="flex items-center justify-end space-x-3">
            <button @click="cancelDelete" :disabled="isDeleting" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button @click="confirmDelete" :disabled="isDeleting" class="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
              <svg v-if="isDeleting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ isDeleting ? 'Eliminando...' : 'Sí, Eliminar' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/auth/auth.js'
import PermissionsService from '@/services/auth/permissions.js'
import { toast } from 'vue-sonner'

export default {
  name: 'ManageAccount',

  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],

  data() {
    return {
      // Estados de la UI
      isLoading: false,
      showForm: false,
      isEditing: false,
      editingUserId: null,

      //   NUEVOS ESTADOS PARA ELIMINACIÓN
      showDeleteModal: false,
      userToDelete: null,
      isDeleting: false,

      // Estados de filtros
      searchText: '',
      selectedEmpresa: '',
      selectedTipoUsuario: '',

      // Estados de dropdown
      dropdownOpen: {
        empresa: false,
        tipoUsuario: false
      },

      //   NUEVOS ESTADOS PARA LOCALES
      selectLocales: [],              // Todos los locales
      localesDisponibles: [],         // Locales filtrados sin encargado
      loadingLocales: false,          // Estado de carga de locales

      // Datos del formulario
      formData: {
        email: '',
        nombre: '',
        apellido: '',
        rut: '',
        telefono: '',
        empresa_id: '',
        empresa_nombre: '',
        tipoUsuario_id: '',
        is_active: true,
        //   NUEVOS CAMPOS PARA EDICIÓN
        password: '',
        password2: '',
        local_asignado_id: ''  //   NUEVO CAMPO
      },

      // Datos de la aplicación
      usuarios: [],
      tipoUsuarios: [],
      selectEmpresas: [],
      // Estados de carga
      loadingUsers: false,
      loadingTipoUsuarios: false,
      loadingSelectEmpresas: false,
    }
  },

  computed: {
    // ========================================
    // PERMISOS COMPUTADOS
    // ========================================
    puedeCrearUsuario() {
      this.permisosVersion
      return PermissionsService.can('usuarios', 'crear')
    },
    puedeEditarUsuario() {
      this.permisosVersion
      return PermissionsService.can('usuarios', 'editar')
    },
    puedeEliminarUsuario() {
      this.permisosVersion
      return PermissionsService.can('usuarios', 'eliminar')
    },
    // ========================================
    /**
     * Verifica si el formulario tiene todos los campos requeridos
     */
    isFormValid() {
      const basicValid = this.formData.email &&
        this.formData.nombre &&
        this.formData.apellido &&
        this.formData.empresa_id &&
        this.formData.tipoUsuario_id;

      //   VALIDACIÓN ADICIONAL PARA EDICIÓN CON CONTRASEÑA
      if (this.isEditing && this.formData.password) {
        return basicValid &&
          this.formData.password === this.formData.password2 &&
          this.formData.password.length >= 8;
      }

      return basicValid;
    },

    /**
     * Usuarios filtrados según criterios de búsqueda
     */
    usuariosFiltrados() {
      let result = [...this.usuarios];

      // Filtro de búsqueda global
      if (this.searchText) {
        const search = this.searchText.toLowerCase();
        result = result.filter(usuario =>
          (usuario.nombre && usuario.nombre.toLowerCase().includes(search)) ||
          (usuario.apellido && usuario.apellido.toLowerCase().includes(search)) ||
          (usuario.email && usuario.email.toLowerCase().includes(search)) ||
          (usuario.rut && usuario.rut.toLowerCase().includes(search)) ||
          (this.getEmpresaNombre(usuario).toLowerCase().includes(search)) ||
          (this.getTipoUsuario(usuario.tipo_usuario).toLowerCase().includes(search))
        );
      }

      // Filtro por empresa
      if (this.selectedEmpresa) {
        result = result.filter(usuario => {
          if (!usuario.empresa) return false;
          if (typeof usuario.empresa === 'object') {
            return usuario.empresa && usuario.empresa.id === this.selectedEmpresa;
          }
          return usuario.empresa === this.selectedEmpresa;
        });
      }

      // Filtro por tipo de usuario
      if (this.selectedTipoUsuario) {
        result = result.filter(usuario => usuario.tipo_usuario === this.selectedTipoUsuario);
      }

      return result;
    },

    /**
     * Verifica si hay filtros activos
     */
    hasActiveFilters() {
      return !!(this.searchText || this.selectedEmpresa || this.selectedTipoUsuario);
    },

    empresasUnicas() {
      const empresas = this.selectEmpresas.filter(e => this.usuarios.some(u => {
        if (!u.empresa) return false;
        if (typeof u.empresa === 'object') {
          return u.empresa && u.empresa.id === e.id;
        }
        return u.empresa === e.id;
      }));
      return empresas.sort((a, b) => a.nombre_empresa.localeCompare(b.nombre_empresa));
    },

    tiposUsuarioUnicos() {
      const tipos = this.tipoUsuarios.filter(t => this.usuarios.some(u => u.tipo_usuario === t.id));
      return tipos.sort((a, b) => a.nombre.localeCompare(b.nombre));
    }
  },

  methods: {
    /**
     * Cierra el modal y resetea todo
     */
    closeModal() {
      document.body.style.overflow = ''
      this.resetAll();
      this.$emit('close');
    },

    /**
     * Resetea todos los estados
     */
    resetAll() {
      this.showForm = false;
      this.showDeleteModal = false;
      this.userToDelete = null;
      this.resetForm();
      this.clearAllFilters();
    },

    /**
     * Resetea solo el formulario
     */
    resetForm() {
      this.formData = {
        email: '',
        nombre: '',
        apellido: '',
        rut: '',
        telefono: '',
        empresa_id: '',
        empresa_nombre: '',
        tipoUsuario_id: '',
        is_active: true,
        password: '',
        password2: '',
        local_asignado_id: ''  //   LIMPIAR TAMBIÉN EL LOCAL
      };
      this.isEditing = false;
      this.editingUserId = null;

      //   LIMPIAR LOCALES DISPONIBLES
      this.localesDisponibles = [];
    },

    /**
     * Toggle dropdown específico
     */
    toggleDropdown(tipo) {
      Object.keys(this.dropdownOpen).forEach(key => {
        if (key !== tipo) {
          this.dropdownOpen[key] = false;
        }
      });
      this.dropdownOpen[tipo] = !this.dropdownOpen[tipo];
    },

    /**
     * Selecciona una opción y cierra dropdown
     */
    selectOption(tipo, valor) {
      if (tipo === 'empresa') {
        this.selectedEmpresa = valor;
      } else if (tipo === 'tipoUsuario') {
        this.selectedTipoUsuario = valor;
      }
      this.dropdownOpen[tipo] = false;
    },

    /**
     * Limpia todos los filtros
     */
    clearAllFilters() {
      this.searchText = '';
      this.selectedEmpresa = '';
      this.selectedTipoUsuario = '';
      Object.keys(this.dropdownOpen).forEach(key => {
        this.dropdownOpen[key] = false;
      });
    },
    /**
     * Muestra el formulario para crear nuevo usuario
     */
    showCreateForm() {
      this.resetForm();
      this.showForm = true;
    },

    /**
     * Cancela el formulario y vuelve a la tabla
     */
    cancelForm() {
      this.resetForm();
      this.showForm = false;
    },

    // ========================================
    //   MÉTODOS DE LOCALES NUEVOS
    // ========================================

    /**
     * Formatea el RUT mientras el usuario escribe
     */
    formatRut(event) {
      let value = event.target.value;

      // 1. Limpiar todo excepto números y 'k' o 'K'
      let cleanValue = value.replace(/[^0-9kK]/gi, '');

      // 2. Limitar la longitud a 9 caracteres (8 para el cuerpo, 1 para el DV)
      if (cleanValue.length > 9) {
        cleanValue = cleanValue.substring(0, 9);
      }

      // 3. Separar cuerpo y dígito verificador
      let body = cleanValue.slice(0, -1);
      let dv = cleanValue.slice(-1);

      // 4. Si el cuerpo contiene 'k' o 'K', es inválido. Se trunca.
      if (/[kK]/.test(body)) {
        body = body.replace(/[kK]/gi, '');
      }

      // 5. Formatear el cuerpo con puntos
      const formattedBody = body.length > 0 ? new Intl.NumberFormat('de-DE').format(body) : body;

      // 6. Reconstruir el RUT
      let finalRut = formattedBody;
      if (cleanValue.length > body.length) {
        finalRut += `-${dv.toUpperCase()}`;
      }

      this.formData.rut = finalRut;
    },

    /**
     *   NUEVO MÉTODO: Cargar todos los locales
     */
    async loadSelectLocales() {
      if (this.loadingLocales) return;

      this.loadingLocales = true;

      try {
        const response = await AuthService.authenticatedRequest('/locales/api/local/');

        // Manejar respuesta paginada o directa
        let locales = [];
        if (Array.isArray(response)) {
          locales = response;
        } else if (response && response.results) {
          locales = response.results;
        } else {
          locales = [];
        }

        // Mapear locales con información adicional
        this.selectLocales = locales.map(local => ({
          id: local.id,
          numero_local: local.numero_local,
          direccion: local.direccion,
          id_encargado: local.id_encargado,
          id_empresa: local.id_empresa,
          sin_encargado: !local.id_encargado || local.id_encargado === null,
          media: local.media
        }));

        // Filtrar locales según empresa seleccionada
        this.filtrarLocalesPorEmpresa();

      } catch (error) {
        toast.error('No se pudieron cargar los locales. Intente nuevamente.');
        this.selectLocales = [];
        this.localesDisponibles = [];
      } finally {
        this.loadingLocales = false;
      }
    },

    /**
     *   Filtrar locales por empresa seleccionada
     */
    filtrarLocalesPorEmpresa() {
      if (!this.formData.empresa_id || !this.selectLocales.length) {
        this.localesDisponibles = [];
        return;
      }

      const empresaId = parseInt(this.formData.empresa_id);

      // Filtrar locales de la empresa seleccionada que NO tengan encargado
      this.localesDisponibles = this.selectLocales.filter(local => {
        const perteneceAEmpresa = local.id_empresa === empresaId;
        const sinEncargado = local.sin_encargado;

        return perteneceAEmpresa && sinEncargado;
      });

      // Limpiar selección de local si no está disponible
      if (this.formData.local_asignado_id) {
        const localSeleccionado = this.localesDisponibles.find(l => l.id === parseInt(this.formData.local_asignado_id));
        if (!localSeleccionado) {
          this.formData.local_asignado_id = '';
        }
      }
    },

    /**
     *   Obtener texto de opciones de local
     */
    getOpcionesLocalTexto() {
      if (this.loadingLocales) {
        return 'Cargando locales...';
      }

      if (!this.formData.empresa_id) {
        return '-- Primero selecciona una empresa --';
      }

      if (this.localesDisponibles.length === 0) {
        return 'No hay locales sin encargado disponibles';
      }

      return '-- Seleccionar Local (Opcional) --';
    },

    /**
     *   Asignar usuario a local
     */
    async asignarUsuarioALocal(usuarioId, localId) {
      try {

        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        const baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';
        const updateUrl = `${baseURL}/locales/api/local/${localId}/`;

        // Obtener datos actuales del local
        const localActual = this.selectLocales.find(l => l.id === localId);
        if (!localActual) {
          throw new Error('Local no encontrado para asignación');
        }

        // Preparar datos para actualizar solo el encargado
        const updateData = {
          id_encargado: parseInt(usuarioId)
        };


        const response = await fetch(updateUrl, {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          },
          body: JSON.stringify(updateData)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error actualizando local (${response.status}): ${errorText}`);
        }

        const responseData = await response.json();

        // Actualizar el local en la lista local
        const localIndex = this.selectLocales.findIndex(l => l.id === localId);
        if (localIndex !== -1) {
          this.selectLocales[localIndex].id_encargado = parseInt(usuarioId);
          this.selectLocales[localIndex].sin_encargado = false;
        }

        return responseData;

      } catch (error) {
        console.error('❌ Error asignando usuario a local');
        throw error;
      }
    },

    // ========================================
    //   MÉTODOS DE EDICIÓN IMPLEMENTADOS
    // ========================================

    /**
     *   Prepara el formulario para editar un usuario
     * @param {Object} usuario - Usuario a editar
     */
    editUser(usuario) {
      try {
        // Configurar modo edición
        this.isEditing = true;
        this.editingUserId = usuario.id;

        //   PRELLENAR FORMULARIO CON DATOS DEL USUARIO
        this.formData = {
          email: usuario.email || '',
          nombre: usuario.nombre || '',
          apellido: usuario.apellido || '',
          rut: usuario.rut || '',
          telefono: usuario.telefono ? this.formatTelefonoDisplay(usuario.telefono) : '',
          empresa_id: usuario.empresa_id || usuario.empresa || '',
          empresa_nombre: this.getEmpresaNombre(usuario),
          tipoUsuario_id: usuario.tipo_usuario || '',
          is_active: usuario.is_active !== false,
          password: '',
          password2: '',
          local_asignado_id: ''  // No prellenar en edición
        };

        // Mostrar formulario
        this.showForm = true;

      } catch (error) {
        toast.error('Error al cargar datos del usuario para edición');
      }
    },

    /**
     *   Actualiza un usuario existente
     */
    async updateUser() {
      this.isLoading = true;

      try {
        //   VALIDACIONES ESPECÍFICAS PARA EDICIÓN
        if (!this.editingUserId) {
          throw new Error('ID de usuario no válido para edición');
        }

        if (!this.formData.tipoUsuario_id) {
          throw new Error('Debe seleccionar un tipo de usuario');
        }

        if (!this.formData.empresa_id) {
          throw new Error('Debe seleccionar una empresa');
        }

        //   VALIDACIÓN DE CONTRASEÑAS SI SE PROPORCIONAN
        if (this.formData.password || this.formData.password2) {
          if (this.formData.password !== this.formData.password2) {
            throw new Error('Las contraseñas no coinciden');
          }

          if (this.formData.password.length < 8) {
            throw new Error('La nueva contraseña debe tener al menos 8 caracteres');
          }
        }

        // Obtener tokens
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        //   PREPARAR DATOS PARA ACTUALIZACIÓN
        const updateData = {
          email: this.formData.email.trim(),
          nombre: this.formData.nombre.trim(),
          apellido: this.formData.apellido.trim(),
          rut: this.formData.rut ? this.formData.rut.trim() : null,
          telefono: this.formData.telefono ? parseInt(this.formData.telefono.replace(/\D/g, '')) : null,
          empresa: parseInt(this.formData.empresa_id),
          tipo_usuario: parseInt(this.formData.tipoUsuario_id),
          is_active: Boolean(this.formData.is_active)
        };

        //   AGREGAR CONTRASEÑA SOLO SI SE PROPORCIONA
        if (this.formData.password && this.formData.password.trim()) {
          updateData.password = this.formData.password.trim();
          updateData.password2 = this.formData.password2.trim();
        }

        this.baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';
        const updateUrl = `${this.baseURL}/usuarios/api/usuarios/${this.editingUserId}/`;

        //   REALIZAR PETICIÓN PATCH
        const response = await fetch(updateUrl, {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          },
          body: JSON.stringify(updateData)
        });

        const responseText = await response.text();
        if (!response.ok) {
          let errorData;
          try {
            errorData = JSON.parse(responseText);
          } catch (parseError) {
            throw new Error(`Error del servidor (${response.status}): ${responseText}`);
          }
          //   MANEJO DETALLADO DE ERRORES
          let mensajeError = 'Error al actualizar usuario:\n';

          if (errorData.email) {
            const emailErrors = Array.isArray(errorData.email) ? errorData.email : [errorData.email];
            mensajeError += `• Email: ${emailErrors.join(', ')}\n`;
          }

          if (errorData.empresa) {
            const empresaErrors = Array.isArray(errorData.empresa) ? errorData.empresa : [errorData.empresa];
            mensajeError += `• Empresa: ${empresaErrors.join(', ')}\n`;
          }

          if (errorData.tipo_usuario) {
            const tipoErrors = Array.isArray(errorData.tipo_usuario) ? errorData.tipo_usuario : [errorData.tipo_usuario];
            mensajeError += `• Tipo de usuario: ${tipoErrors.join(', ')}\n`;
          }

          if (errorData.password) {
            const passwordErrors = Array.isArray(errorData.password) ? errorData.password : [errorData.password];
            mensajeError += `• Contraseña: ${passwordErrors.join(', ')}\n`;
          }

          if (errorData.non_field_errors) {
            const generalErrors = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors : [errorData.non_field_errors];
            mensajeError += `• ${generalErrors.join(', ')}\n`;
          }

          throw new Error(mensajeError.trim() || `Error del servidor (${response.status})`);
        }

        //   PARSEAR RESPUESTA EXITOSA
        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch (parseError) {
          throw new Error('Respuesta del servidor no válida');
        }
        const userIndex = this.usuarios.findIndex(u => u.id === this.editingUserId);
        if (userIndex !== -1) {
          this.usuarios[userIndex] = responseData;
        }

        const mensajeExito = `Usuario ${responseData.nombre} ${responseData.apellido} actualizado exitosamente.`

        toast.success(mensajeExito);

        // Limpiar formulario y volver a tabla
        this.resetForm();
        this.showForm = false;

      } catch (error) {
        console.error('❌ Error actualizando usuario:');
        toast.error(error.message);

      } finally {
        this.isLoading = false;
      }
    },

    // ========================================
    //   MÉTODOS DE ELIMINACIÓN IMPLEMENTADOS
    // ========================================

    /**
     *   Muestra modal de confirmación para eliminar usuario
     * @param {Object} usuario - Usuario a eliminar
     */
    showDeleteConfirmation(usuario) {
      console.log('🗑️ Solicitando eliminación de usuario');
      this.userToDelete = usuario;
      this.showDeleteModal = true;
    },

    /**
     *   Cancela la eliminación
     */
    cancelDelete() {
      console.log('❌ Eliminación cancelada');
      this.showDeleteModal = false;
      this.userToDelete = null;
      this.isDeleting = false;
    },

    /**
     *   Confirma y ejecuta la eliminación del usuario
     */
    async confirmDelete() {
      if (!this.userToDelete) {
        toast.info('No hay usuario seleccionado para eliminar');
        return;
      }

      this.isDeleting = true;

      try {

        // Obtener tokens
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        this.baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';
        const deleteUrl = `${this.baseURL}/usuarios/api/usuarios/${this.userToDelete.id}/`;


        //   REALIZAR PETICIÓN DELETE
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          }
        });


        if (!response.ok) {
          const responseText = await response.text();

          let errorMessage = `Error del servidor (${response.status})`;

          // Intentar parsear error si es JSON
          try {
            const errorData = JSON.parse(responseText);
            if (errorData.detail) {
              errorMessage = errorData.detail;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch (parseError) {
            // Usar mensaje genérico si no se puede parsear
          }

          throw new Error(errorMessage);
        }


        //   REMOVER USUARIO DE LA LISTA LOCAL
        const userIndex = this.usuarios.findIndex(u => u.id === this.userToDelete.id);
        if (userIndex !== -1) {
          this.usuarios.splice(userIndex, 1);
        }

        // Mostrar mensaje de éxito
        const mensajeExito = `Usuario ${this.userToDelete.nombre} ${this.userToDelete.apellido} eliminado exitosamente.`;

        toast.success(mensajeExito);

        // Cerrar modal
        this.showDeleteModal = false;
        this.userToDelete = null;

      } catch (error) {
        console.error('❌ Error eliminando usuario');
        toast.error(`Error al eliminar usuario: ${error.message}`);

      } finally {
        this.isDeleting = false;
      }
    },

    // ========================================
    //   MÉTODOS EXISTENTES ACTUALIZADOS
    // ========================================

    /**
     * Formatea el número de teléfono al estilo chileno mientras se escribe
     * Formato: +56 9 1234 5678
     */
    formatTelefono() {
      let input = this.formData.telefono; // No se necesita 'event'

      // 1. Limpiar todo excepto números y el signo '+'
      let digits = input.replace(/\D/g, '');

      // 2. Si no tiene '+', se lo añade por defecto.
      if (!input.startsWith('+') && digits.length > 0) {
        digits = '+' + digits;
      } else if (input.startsWith('+')) {
        digits = '+' + digits;
      }

      // Limitar la longitud total
      if (digits.length > 16) {
        digits = digits.substring(0, 16);
      }

      // 3. Aplicar formato con espacios para una mejor UX
      let formatted = '';
      if (digits.startsWith('+56')) { // Formato específico para Chile
        const code = digits.substring(0, 3); // +56
        const number = digits.substring(3);

        if (number.startsWith('9') && number.length > 1) {
          formatted = `${code} ${number.charAt(0)} ${number.substring(1, 5)} ${number.substring(5, 9)}`;
        } else if (number.length > 0) {
          formatted = `${code} ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5, 9)}`;
        } else {
          formatted = code;
        }
      } else {
        formatted = digits;
      }

      this.formData.telefono = formatted.trim();
    },
    /**
     * Formatea el número de teléfono para visualización
     */
    formatTelefonoDisplay(telefono) {
      if (!telefono) return '';

      // Convertir a string si es número
      const telefonoStr = typeof telefono === 'number' ? telefono.toString() : telefono;

      // Limpiar y estandarizar
      let digits = telefonoStr.replace(/\D/g, '');
      if (!telefonoStr.startsWith('+')) {
        digits = '56' + digits; // Asumir Chile si no hay prefijo
      }

      if (digits.startsWith('56')) {
        const number = digits.substring(2);
        if (number.startsWith('9')) {
          return `+56 9 ${number.substring(1, 5)} ${number.substring(5, 9)}`.trim();
        } else {
          return `+56 ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5, 9)}`.trim();
        }
      }
      return telefonoStr; // Devolver como está si no es formato chileno
    },
    /**
     * Maneja el envío del formulario
     */
    async submitForm() {
      if (!this.isFormValid) {
        this.showError('Por favor completa todos los campos requeridos');
        return;
      }

      this.isLoading = true;

      try {
        if (this.isEditing) {
          await this.updateUser();
        } else {
          await this.createUser();
        }
      } catch (error) {
        console.error('Error en el formulario:', error);
        this.showError(`Error: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     *   Crea un nuevo usuario - IMPLEMENTACIÓN REAL CON LOCALES
     */
    async createUser() {
      try {
        // Validaciones previas
        if (!this.formData.tipoUsuario_id) {
          toast.error('Debe seleccionar un tipo de usuario')
          throw new Error('Debe seleccionar un tipo de usuario');
        }

        if (!this.formData.empresa_id) {
          toast.error('Debe seleccionar una empresa')
          throw new Error('Debe seleccionar una empresa');
        }

        // Verificar que el tipo seleccionado existe
        const tipoSeleccionado = this.tipoUsuarios.find(t => t.id == this.formData.tipoUsuario_id);
        if (!tipoSeleccionado) {
          toast.error('Tipo de usuario no válido')
          throw new Error(`Tipo de usuario no válido: ${this.formData.tipoUsuario_id}`);
        }

        // Verificar que la empresa seleccionada existe
        const empresaSeleccionada = this.selectEmpresas.find(e => e.id == this.formData.empresa_id);
        if (!empresaSeleccionada) {
          toast.error('Empresa no válida')
          throw new Error(`Empresa no válida: ${this.formData.empresa_id}`);
        }

        //   VALIDAR LOCAL SI SE SELECCIONÓ
        let localSeleccionado = null;
        if (this.formData.local_asignado_id) {
          localSeleccionado = this.localesDisponibles.find(l => l.id == this.formData.local_asignado_id);

          if (!localSeleccionado) {
            toast.error('Local seleccionado no es válido')
            throw new Error('Local seleccionado no es válido');
          }

          if (localSeleccionado.id_empresa !== parseInt(this.formData.empresa_id)) {
            toast.error('El local no pertenece a la empresa seleccionada')
            throw new Error('El local no pertenece a la empresa seleccionada');
          }

          if (!localSeleccionado.sin_encargado) {
            toast.error('El local seleccionado ya tiene encargado asignado')
            throw new Error('El local seleccionado ya tiene encargado asignado');
          }
        }


        // Obtener tokens y preparar datos
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        const userData = {
          email: this.formData.email.trim(),
          nombre: this.formData.nombre.trim(),
          apellido: this.formData.apellido.trim(),
          rut: this.formData.rut ? this.formData.rut.trim() : null,
          telefono: this.formData.telefono ? parseInt(this.formData.telefono.replace(/\D/g, '')) : null,
          empresa: parseInt(this.formData.empresa_id),
          tipo_usuario: parseInt(this.formData.tipoUsuario_id),
          is_active: Boolean(this.formData.is_active)
        };

        this.baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';

        //   1. CREAR USUARIO
        const response = await fetch(`${this.baseURL}/usuarios/api/usuarios/`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          },
          body: JSON.stringify(userData)
        });

        const responseText = await response.text();

        if (!response.ok) {
          let errorData;
          try {
            errorData = JSON.parse(responseText);
          } catch {
            toast.error(`Error del servidor (${response.status}): ${responseText}`)
            throw new Error(`Error del servidor (${response.status}): ${responseText}`);
          }

          let mensajeError = 'Error al crear usuario:\n';
          if (errorData.empresa) {
            mensajeError += `• Empresa: ${errorData.empresa}\n`;
          }

          toast.error(mensajeError.trim());
          throw new Error(mensajeError.trim());
        }

        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch {
          toast.error('Respuesta del servidor no válida')
          throw new Error('Respuesta del servidor no válida');
        }

        //   2. ASIGNAR LOCAL (si corresponde)
        if (this.formData.local_asignado_id && responseData.id) {
          try {
            await this.asignarUsuarioALocal(responseData.id, this.formData.local_asignado_id);
          } catch (localError) {
            const warn = `Usuario creado pero hubo un error asignándolo al local: ${localError.message}`;
            toast.error(warn)
            this.showError(warn)
          }
        }

        // Agregar usuario a la lista, limpiar formularios, etc.
        this.usuarios.push(responseData);
        this.filtrarLocalesPorEmpresa();
        this.resetForm();
        this.showForm = false;

        //   ⚡ MENSAJE DE ÉXITO FINAL
        let mensajeExito =
          `Usuario ${responseData.nombre} ${responseData.apellido} creado exitosamente.`;

        if (localSeleccionado) {
          mensajeExito += `\nAsignado al Local ${localSeleccionado.numero_local} (${localSeleccionado.direccion})`;
        }

        toast.success(mensajeExito);

      } catch (error) {
        console.error('❌ Error creando usuario:', error);
        this.showError(error.message);
        toast.error(error.message);
        throw error;
      }
    },

    /**
     * Carga la lista de usuarios
     */
    async loadUsers() {
      if (this.loadingUsers) return;
      this.loadingUsers = true;
      try {
        const response = await AuthService.authenticatedRequest('/usuarios/api/usuarios/');

        // ADAPTACIÓN A SISTEMA DE PAGINACION:
        this.usuarios = response.results || [];  // Extraer array de usuarios de la respuesta paginada       


        console.log('  Usuarios cargados:', this.usuarios.length);
      } catch (error) {
        console.error('❌ Error cargando usuarios');
        this.usuarios = [];
      } finally {
        this.loadingUsers = false;
      }
    },


    /**
     * Carga la lista de tipos de usuario desde la API
     */
    async loadTipoUsuarios() {
      if (this.loadingTipoUsuarios) return;

      this.loadingTipoUsuarios = true;

      try {
        const response = await AuthService.authenticatedRequest('/usuarios/api/tipos_usuario/');

        // LA API DEVUELVE RESPUESTA PAGINADA CON results
        if (response && Array.isArray(response.results)) {
          // MAPEAR nombre_tipo a nombre para compatibilidad
          this.tipoUsuarios = response.results.map(tipo => ({
            id: tipo.id,
            nombre: tipo.nombre_tipo, //   MAPEAR para que funcione con el resto del código
            nombre_tipo: tipo.nombre_tipo, // Mantener original
            descripcion: tipo.descripcion_tipo,
            permisos: tipo.permisos
          }));

        } else {
          throw new Error('Formato de respuesta no válido');
        }

      } catch (error) {
        console.error('❌ Error cargando tipos de usuario');

        // FALLBACK ACTUALIZADO CON DATOS REALES
        this.tipoUsuarios = [
          { id: 1, nombre: 'Cliente', nombre_tipo: 'Cliente' },
          { id: 2, nombre: 'Administrador', nombre_tipo: 'Administrador' },
          { id: 4, nombre: 'Gestor/Supervisor', nombre_tipo: 'Gestor/Supervisor' },
          { id: 5, nombre: 'Operador/Técnico', nombre_tipo: 'Operador/Técnico' },
          { id: 6, nombre: 'Invitado/Auditor', nombre_tipo: 'Invitado/Auditor' },
          { id: 7, nombre: 'Encargado', nombre_tipo: 'Encargado' } //   NUEVO TIPO
        ];


      } finally {
        this.loadingTipoUsuarios = false;
      }
    },

    /**
     * Carga la lista de empresas desde la API con manejo de paginación
     */
    async loadSelectEmpresas() {
      if (this.loadingSelectEmpresas) return;

      this.loadingSelectEmpresas = true;

      try {
        let allEmpresas = [];
        let nextPage = '/empresas/api/empresa/';

        // Manejar paginación
        while (nextPage) {
          const response = await AuthService.authenticatedRequest(nextPage);

          // Manejar diferentes formatos de respuesta
          let empresas = [];
          if (Array.isArray(response)) {
            empresas = response;
            nextPage = null;
          } else if (response && response.results) {
            empresas = response.results;
            nextPage = response.next;
          } else {
            console.error('Formato de respuesta inesperado');
            break;
          }

          allEmpresas = [...allEmpresas, ...empresas];

          // Limitar para no hacer demasiadas requests en desarrollo
          if (allEmpresas.length > 100) break;
        }

        console.log(`  ${allEmpresas.length} empresas cargadas correctamente`);

        // Mapear empresas con validación
        this.selectEmpresas = allEmpresas.map(empresa => {
          if (!empresa.id || !empresa.nombre_empresa) {
            console.warn('Empresa con datos incompletos:', empresa);
          }
          return {
            id: empresa.id,
            nombre_empresa: empresa.nombre_empresa || 'Nombre no disponible'
          };
        });

      } catch (error) {
        console.error('❌ Error cargando empresas');
        toast.error('No se pudieron cargar las empresas. Intente nuevamente.');
        this.selectEmpresas = [];
      } finally {
        this.loadingSelectEmpresas = false;
      }
    },

    /**
     * Obtiene el nombre de empresa de forma robusta
     */
    getEmpresaNombre(usuario) {
      // Si no hay usuario o no hay empresas cargadas
      if (!usuario || !this.selectEmpresas.length) {
        return 'Datos no disponibles';
      }

      let empresaId = null;

      // Determinar el ID de la empresa basado en diferentes estructuras de datos
      if (usuario.empresa !== null && usuario.empresa !== undefined) {
        if (typeof usuario.empresa === 'number') {
          empresaId = usuario.empresa;
        } else if (typeof usuario.empresa === 'object' && usuario.empresa.id) {
          empresaId = usuario.empresa.id;
        } else if (typeof usuario.empresa === 'string') {
          empresaId = parseInt(usuario.empresa);
        }
      }

      // Si no encontramos el ID por los métodos anteriores, usar empresa_id
      if ((empresaId === null || isNaN(empresaId)) && usuario.empresa_id) {
        empresaId = typeof usuario.empresa_id === 'number'
          ? usuario.empresa_id
          : parseInt(usuario.empresa_id);
      }

      // Buscar la empresa por ID
      if (empresaId && !isNaN(empresaId)) {
        const empresa = this.selectEmpresas.find(e => e.id === empresaId);
        if (empresa) {
          return empresa.nombre_empresa;
        } else {
          console.warn(`⚠️ Empresa con ID no encontrada en selectEmpresas`);
        }
      }

      // Si no se encontró la empresa, mostrar información de debug
      console.warn('No se pudo determinar la empresa para el usuario');
      return `Sin empresa (Usuario ID: ${usuario.id})`;
    },

    getEmpresaNombrePorId(empresaId) {
      if (!empresaId || !this.selectEmpresas.length) {
        return 'N/A';
      }
      const empresa = this.selectEmpresas.find(e => e.id === empresaId);
      return empresa ? empresa.nombre_empresa : `Empresa ID ${empresaId}`;
    },


    /**
     * Obtiene el nombre del tipo de usuario basado en el ID
     */
    getTipoUsuario(tipoUsuarioId) {
      if (!tipoUsuarioId) return 'Sin tipo';

      if (!this.tipoUsuarios || this.tipoUsuarios.length === 0) {
        return `Tipo ${tipoUsuarioId}`;
      }

      const tipo = this.tipoUsuarios.find(t => {
        return t && (t.id === tipoUsuarioId || t.id === parseInt(tipoUsuarioId));
      });

      if (tipo) {
        // USAR nombre (mapeado) o nombre_tipo (original)
        return tipo.nombre || tipo.nombre_tipo;
      } else {
        return `Tipo ${tipoUsuarioId}`;
      }
    },

    /**
     * Genera iniciales a partir de nombre y apellido
     */
    getInitials(nombre, apellido) {
      const inicial1 = nombre ? nombre.charAt(0).toUpperCase() : '';
      const inicial2 = apellido ? apellido.charAt(0).toUpperCase() : '';
      return inicial1 + inicial2 || 'U';
    },

    /**
     * Muestra notificación de éxito
     */
    showSuccess(message) {
      alert(`  ${message}`);
    },

    /**
     * Muestra notificación de error
     */
    showError(message) {
      alert(`❌ ${message}`);
    }
  },

  mounted() {
    // Event listener para click-away de los dropdowns
    document.addEventListener('click', this.handleClickOutside);
  },

  watch: {
    /**
     *   DEBUG: Watch para showDeleteModal
     */
    showDeleteModal: {
      handler(newValue, oldValue) {
        console.log('🔄 showDeleteModal cambió:', {
          anterior: oldValue,
          nuevo: newValue,
          userToDelete: this.userToDelete
        });
      },
      immediate: false
    },

    /**
     *   MODIFICAR EL WATCHER DE formData.empresa_nombre EXISTENTE
     */
    'formData.empresa_nombre': {
      handler(nombreEmpresa) {
        if (nombreEmpresa && this.selectEmpresas.length > 0) {
          const empresaSeleccionada = this.selectEmpresas.find(
            empresa => empresa.nombre_empresa === nombreEmpresa
          );

          if (empresaSeleccionada) {
            this.formData.empresa_id = empresaSeleccionada.id;
            console.log('🏢 Empresa mapeada:', {
              nombre: nombreEmpresa,
              id: empresaSeleccionada.id
            });

            //   FILTRAR LOCALES CUANDO CAMBIA LA EMPRESA
            this.filtrarLocalesPorEmpresa();

          } else {
            this.formData.empresa_id = '';
            this.localesDisponibles = [];
          }
        } else {
          this.formData.empresa_id = '';
          this.localesDisponibles = [];
        }
      }
    },

    isOpen: {
      async handler(newValue) {
        // Bloquear/desbloquear scroll del body
        document.body.style.overflow = newValue ? 'hidden' : ''

        if (newValue && this.usuarios.length === 0) {
          console.log('🔍 Modal abierto - cargando datos iniciales...');

          try {
            await Promise.all([
              this.loadUsers(),
              this.loadTipoUsuarios(),
              this.loadSelectEmpresas(),
              this.loadSelectLocales()
            ]);

            console.log('  Datos iniciales cargados (incluyendo locales)');

          } catch (error) {
            console.error('❌ Error cargando datos iniciales:', error);
          }
        }
      },
      immediate: true
    }
  },

  beforeUnmount() {
    document.body.style.overflow = ''
    // Limpiar event listener
    document.removeEventListener('click', this.handleClickOutside);
    this.resetAll();
  }
}
</script>

<style scoped>
/* Transiciones para el modal */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
  transform: scale(0.9);
}

.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>